const { trapFocus, disableTrapFocus, setCookie } = window.BAO.Utils

export class ShippingSelector extends window.BAO.CustomElement() {
  constructor () {
    super()

    this.countryCode = this.noLocationStored
      ? this.dataset.store
      : window.localStorage.getItem(this.userLocationName)

    // eslint-disable-next-line compat/compat
    const params = new Proxy(new URLSearchParams(window.location.search), {
      get: (searchParams, prop) => searchParams.get(prop),
    })
    const countryParam = params.country

    if (countryParam !== null) this.countryCode = countryParam

    this.hasOptions = false
    this.optionsHtml = ''

    this.selectedOption = null
    this.selectedUrl = null
  }

  connectedCallback () {
    super.connectedCallback()

    this.regionEls = this.els?.region?.elements

    this.constructOptionsHtml()
  }

  setupListeners () {
    super.setupListeners()

    this.onButtonClick = this.onButtonClick.bind(this)

    if (this.els?.region?.exists) {
      this.els.region.elements.forEach(regionEl => {
        this.listeners.add(regionEl, 'click', this.onButtonClick)
      })
    }

    this.onSubmit = this.onSubmit.bind(this)

    if (this.externalEls?.submit?.exists) {
      this.listeners.add(
        this.externalEls.submit.element,
        'click',
        this.onSubmit,
      )
    }

    this.onCloseClick = this.onCloseClick.bind(this)

    if (this.externalEls?.close.exists) {
      this.externalEls.close.elements.forEach(closeExternalEl => {
        this.listeners.add(closeExternalEl, 'click', this.onCloseClick)
      })
    }

    this.onSearch = this.onSearch.bind(this)

    if (this.externalEls?.search.exists) {
      this.externalEls.search.elements.forEach(searchExternalEl => {
        this.listeners.add(searchExternalEl, 'input', this.onSearch)
      })
    }

    this.onResetClick = this.onResetClick.bind(this)

    if (this.externalEls?.reset?.exists) {
      this.externalEls.reset.elements.forEach(resetExternalEl => {
        this.listeners.add(resetExternalEl, 'click', this.onResetClick)
      })
    }

    // Language els are dynamically created, so we delegate the listener to the main element.
    this.listeners.add(this, 'click', this.onLanguageClick.bind(this))
  }

  onLanguageClick (e) {
    const languageEl = e.target.closest(this.els?.language?.selector)
    if (!languageEl) return

    const languageSubdirectory = languageEl.dataset.languageSubdirectory

    if (languageSubdirectory) {
      const itemContainertEl = languageEl?.closest(this.els?.item?.selector)
      const itemButtonEl = itemContainertEl?.querySelector(
        this.els?.button?.selector,
      )

      itemButtonEl.dataset.languageSubdirectory = languageSubdirectory

      // Re-build URL with new language subdirectory
      this.buildUrl(itemButtonEl)
    }
  }

  onResetClick () {
    const items = this.querySelectorAll('.shp-ShippingSelector_Item')

    items.forEach(item => {
      item.style.display = ''
    })

    if (this.externalEls?.search.exists) {
      this.externalEls.search.elements.forEach(searchExternalEl => {
        searchExternalEl.value = ''
        searchExternalEl.focus()
      })
    }

    if (this.externalEls?.reset?.exists) {
      this.externalEls.reset.elements.forEach(resetExternalEl => {
        resetExternalEl.setAttribute('aria-hidden', 'true')
        resetExternalEl.disabled = true
      })
    }
  }

  onSearch (event) {
    const searchValue = event.target.value.toLowerCase()

    if (searchValue !== '') {
      this.classList.add(this.activeSearchClass)
      this.classList.remove(this.transitionsClass)
    } else {
      this.classList.remove(this.activeSearchClass)

      setTimeout(() => {
        this.classList.add(this.transitionsClass)
      }, 500)
      // Adding delay to prevent unwanted transition when reverting back to non-search layout
    }

    const items = this.querySelectorAll('.shp-ShippingSelector_Item')

    items.forEach(item => {
      const itemName = item.querySelector('.shp-ShippingSelector_Name')
      if (!itemName) return

      let itemNameContent = item.querySelector('.shp-ShippingSelector_Name')
        .textContent
      const itemNameContentLower = itemNameContent.toLowerCase()

      itemName.innerHTML = itemName.dataset.name

      if (itemNameContentLower.indexOf(searchValue) > -1) {
        item.style.display = ''

        const itemIndex = itemNameContentLower.indexOf(searchValue)
        itemNameContent = `${itemNameContent.substring(
          0,
          itemIndex,
        )}<mark>${itemNameContent.substring(
          itemIndex,
          itemIndex + searchValue.length,
        )}</mark>${itemNameContent.substring(itemIndex + searchValue.length)}`
        itemName.innerHTML = itemNameContent
      } else {
        item.style.display = 'none'
      }
    })

    if (this.externalEls?.reset?.exists) {
      this.externalEls.reset.elements.forEach(resetExternalEl => {
        resetExternalEl.setAttribute('aria-hidden', searchValue === '')
        resetExternalEl.disabled = searchValue === ''
      })
    }
  }

  onCloseClick (event) {
    event.preventDefault()

    if (this.externalEls?.popup.exists) {
      this.externalEls.popup.elements.forEach(popupExternalEl => {
        popupExternalEl.setAttribute('aria-hidden', true)
      })
    }

    // Refocus on the countries list drawer if it is open.
    if (window.drawers.activeDrawerKey === 'shipping-selector') {
      setTimeout(() => {
        const closeEl = document.querySelector(
          '[data-drawers-close="shipping-selector"]',
        )
        closeEl.focus()
      }, 250)

      setTimeout(() => {
        const element = document.querySelector(
          `site-drawer[key='shipping-selector']`,
        )

        disableTrapFocus()
        trapFocus(element)
      }, 500)
    }
  }

  onSubmit () {
    if (this.selectedUrl) {
      const shippingSelectorUpdate = new CustomEvent('shippingSelectorUpdate', {
        detail: this.countryCode,
      })

      document.dispatchEvent(shippingSelectorUpdate)

      window.localStorage.setItem(this.userLocationName, this.countryCode)

      const isSeperateDomain =
        new URL(this.selectedUrl).origin !== window.location.origin

      if (isSeperateDomain) {
        let redirectUrl = this.selectedUrl

        if (this.shopifyPathname !== '') {
          if (this.shopifyPathname !== '') {
            redirectUrl = redirectUrl.replace(this.shopifyPathname, '')
          }
        }

        this.setRedirectSiteStorage(redirectUrl)
      }

      window.location.href = this.selectedUrl
    }
  }

  onButtonClick (e) {
    const buttonEl = e.target.closest(this.els?.button?.selector)

    if (!buttonEl) return

    if (buttonEl.hasAttribute('aria-selected')) {
      this.resetSelections()

      return
    }

    this.els?.button?.elements.forEach(el => {
      if (el.dataset.code === buttonEl.dataset.code) {
        el.setAttribute('aria-selected', 'true')

        this.selectedOption = el

        this.buildUrl(buttonEl)
        this.checkForInstantSelect(buttonEl)
        this.toggleSubmit(true)
      } else {
        el.removeAttribute('aria-selected')
      }
    })
  }

  resetSelections () {
    this.els?.button?.elements.forEach(el => {
      el.removeAttribute('aria-selected')
    })

    this.selectedOption = null
    this.selectedUrl = null

    this.toggleSubmit(false)
  }

  toggleSubmit (enable) {
    const button = this.externalEls?.submit?.element

    if (button) {
      button.innerText = enable
        ? button.dataset.submitText
        : button.dataset.disabledText
      button.disabled = !enable
    }
  }

  checkForInstantSelect (buttonEl) {
    const subdirectory = buttonEl.dataset?.languageSubdirectory

    if (!subdirectory) {
      this.onSubmit()
    }
  }

  constructOptionsHtml () {
    Object.entries(this.countries).forEach(([key, value]) => {
      const { currency, name, site, region } = value
      const isCurrentSite = key.toLowerCase() === this.countryCode.toLowerCase()

      const optionElement = this.createOptionElement(
        key,
        site,
        isCurrentSite,
        name,
        currency,
      )

      const regionHandle = region
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '-')

      const regionEl = this.regionEls.find(el => {
        return el.dataset.regionHandle === regionHandle
      })

      if (regionEl) {
        regionEl.insertAdjacentHTML('beforeend', optionElement)
      }
    })
  }

  createOptionElement (code, site, isCurrentSite, country, currency) {
    const languageOptionsData = this.retrieveLanguageOptionData(code)

    let languageMarkup = ''

    if (Array.isArray(languageOptionsData) && languageOptionsData.length > 0) {
      const itemsHtml = languageOptionsData
        .map((lang, index) => {
          const isChecked = index === 0 ? 'checked' : ''

          return `
            <li class="shp-ShippingSelector_LanguagesItem">
              <input
                class="shp-ShippingSelector_LanguagesCheckbox"
                type="radio"
                name="LanguagesCheckbox-${country}"
                id="LanguagesCheckbox-${country}-${lang.subdirectory}"
                value="true"
                data-shipping-selector-el="language"
                data-language-subdirectory="${lang.subdirectory}"
                ${isChecked}
              >
              <label
                class="shp-ShippingSelector_LanguagesLabel"
                for="LanguagesCheckbox-${country}-${lang.subdirectory}"
              >
                ${lang.title}
              </label>
            </li>
          `
        })
        .join('')

      languageMarkup = `
        <div class="shp-ShippingSelector_Languages">
          <ul class="shp-ShippingSelector_LanguagesList" data-shipping-selector-el="languages">
            ${itemsHtml}
          </ul>
        </div>
      `
    }

    return `
      <li class="shp-ShippingSelector_Item" data-shipping-selector-el="item">
        <button
          class="shp-ShippingSelector_Button${
            isCurrentSite ? ' shp-ShippingSelector_Button-current' : ''
          }"
          data-shipping-selector-el="button"
          data-code="${code}"
          data-site="${site}"
          data-language-subdirectory="${
            languageOptionsData ? languageOptionsData[0].subdirectory : ''
          }"
        >
          <span class="shp-ShippingSelector_Name" data-name="${country}">${country}</span>
          <span class="shp-ShippingSelector_Currency">${currency}</span>
        </button>

        ${languageMarkup}
      </li>
    `
  }

  retrieveLanguageOptionData (key) {
    key = key.toUpperCase()

    if (
      this.sites[key] &&
      this.sites[key].languages &&
      this.sites[key].languages.length > 1
    ) {
      return this.sites[key].languages
    } else {
      return false
    }
  }

  handleExternalStoreModal () {
    if (this.externalEls?.popup.exists) {
      this.externalEls.popup.elements.forEach(popupExternalEl => {
        popupExternalEl.setAttribute('aria-hidden', false)

        disableTrapFocus()
        trapFocus(popupExternalEl)

        setTimeout(() => {
          popupExternalEl
            .querySelector('[data-shipping-selector-external-el="confirm"]')
            .focus()
        }, 500)
      })
    }
  }

  buildUrl (buttonEl) {
    const site = buttonEl.dataset?.site
    const subdirectory = buttonEl.dataset?.languageSubdirectory
    const country = buttonEl.dataset?.code?.toLowerCase()
    const currency = this.sites[site]?.currency
    const currencyValue = currency ? currency.split('=').pop() : null

    const baseUrl = this.sites[site]?.url

    if (!baseUrl) return

    // Build path by only adding non-empty segments
    const pathParts = []

    if (subdirectory) {
      pathParts.push(subdirectory)
    }

    if (this.shopifyPathname) {
      pathParts.push(this.shopifyPathname.replace(/^\/+/, ''))
    }

    const path = pathParts.join('/')

    const queryParams = new URLSearchParams(window.location.search)
    queryParams.delete('country')
    queryParams.delete('currency')

    if (country) queryParams.set('country', country)
    if (currencyValue) queryParams.set('currency', currencyValue)

    // Final URL
    this.selectedUrl = `${baseUrl.replace(
      /\/$/,
      '',
    )}/${path}?${queryParams.toString()}`
  }

  setRedirectSiteStorage (url) {
    if (url) {
      setCookie(
        this.redirectSiteStorageName,
        url,
        this.storageDurationDays,
        true,
      )
    }
  }

  get shopifyPathname () {
    // Get pathname, check the first part (i.e. /pages), and make sure that it matches the expected
    // Shopify pathnames. If it doesn't, we assume it's a market subdirectory and remove it.
    const url = new URL(window.location.href)

    let pathname = url.pathname
    const pathnameSplit = pathname.split('/')
    if (!this.shopifyPathnames.includes(pathnameSplit[1])) {
      pathname = pathname.replace(`/${pathnameSplit[1]}`, '')
    }

    return pathname
  }

  get noChoiceMade () {
    return (
      window.localStorage.getItem(this.storageName) == null ||
      (window.localStorage.getItem(this.storageName) === undefined &&
        !Shopify.designMode)
    )
  }

  get noLocationStored () {
    return (
      window.localStorage.getItem(this.userLocationName) == null ||
      (window.localStorage.getItem(this.userLocationName) === undefined &&
        !Shopify.designMode)
    )
  }

  get storageName () {
    return 'BAO-store-portrait'
  }

  get userLocationName () {
    return 'BAO-location-portrait'
  }

  get redirectSiteStorageName () {
    return `BAO-country-confirmation-redirect-site`
  }

  get storageDurationDays () {
    return 30 // days
  }

  get activeSearchClass () {
    return `shp-ShippingSelector-searching`
  }

  get transitionsClass () {
    return `shp-ShippingSelector-transition`
  }

  get sites () {
    return window.theme.sites
  }

  get countries () {
    return window.theme.countries
  }

  get shopifyPathnames () {
    return [
      'pages',
      'products',
      'collections',
      'blogs',
      'policies',
      'search',
      'account',
      'cart',
      '404',
    ]
  }
}

if (!customElements.get('shipping-selector')) {
  customElements.define('shipping-selector', ShippingSelector)
}
